import { supabase } from "./churchSupabaseClient";
import { getSermonApiSettings } from "./sermonService";

const SERVICE_ITEMS_TABLE = "service_items";
const ORDER_OF_SERVICE_SOURCE_APPS = new Set(["order-of-service", "hymns"]);
const HYMN_BLOCK_SOURCE_TYPE = "weekly-hymn-block";
const INVITATION_SONG_SLOT_INDEX = 6;
const DEFAULT_HYMN_STORAGE_BUCKET = "global-hymn-files";
const SERVICE_SLIDE_SIGNED_URL_SECONDS = 60 * 60 * 24 * 30;
const SERVICE_SLIDE_SIGNED_URL_CACHE_TTL_MS = 1000 * 60 * 60 * 12;
const SERVICE_SLIDE_SIGNED_URL_CACHE_KEY = "oikos.church.service-slide-signed-urls";
const serviceSlideSignedUrlCache = new Map();

function isMissingRelationError(error) {
  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    (typeof error?.message === "string" &&
      error.message.toLowerCase().includes("does not exist"))
  );
}

function getServiceStorageKey(userId, serviceId) {
  return `oikos.church.service-items.${userId || "guest"}.${serviceId}`;
}

function getServiceSettings() {
  return getSermonApiSettings();
}

function normalizeServiceItem(item = {}) {
  const payload = item.payload || {};

  return {
    ...item,
    payload,
    slides: Array.isArray(payload.slides) ? payload.slides : [],
    itemType: item.type || payload.type || "item",
    sourceApp: item.source_app || item.sourceApp || "service",
  };
}

function getServiceSlideImageCacheKey(bucket = DEFAULT_HYMN_STORAGE_BUCKET, imagePath = "") {
  return `${bucket}::${imagePath}`;
}

function readServiceSlideSignedUrlCache(paths = []) {
  const now = Date.now();
  const signedUrlMap = new Map();

  paths.forEach(({ bucket, imagePath }) => {
    const key = getServiceSlideImageCacheKey(bucket, imagePath);
    const cached = serviceSlideSignedUrlCache.get(key);

    if (cached?.signedUrl && now - Number(cached.cachedAt || 0) < SERVICE_SLIDE_SIGNED_URL_CACHE_TTL_MS) {
      signedUrlMap.set(key, cached.signedUrl);
    } else if (cached) {
      serviceSlideSignedUrlCache.delete(key);
    }
  });

  if (typeof window === "undefined") {
    return signedUrlMap;
  }

  try {
    const raw = window.localStorage.getItem(SERVICE_SLIDE_SIGNED_URL_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};

    paths.forEach(({ bucket, imagePath }) => {
      const key = getServiceSlideImageCacheKey(bucket, imagePath);
      if (signedUrlMap.has(key)) return;

      const cached = parsed[key];
      if (cached?.signedUrl && now - Number(cached.cachedAt || 0) < SERVICE_SLIDE_SIGNED_URL_CACHE_TTL_MS) {
        serviceSlideSignedUrlCache.set(key, cached);
        signedUrlMap.set(key, cached.signedUrl);
      }
    });
  } catch (_error) {
    return signedUrlMap;
  }

  return signedUrlMap;
}

function writeServiceSlideSignedUrlCache(entries = []) {
  if (entries.length === 0) {
    return;
  }

  const now = Date.now();
  entries.forEach(({ bucket, imagePath, signedUrl }) => {
    if (!imagePath || !signedUrl) return;

    serviceSlideSignedUrlCache.set(getServiceSlideImageCacheKey(bucket, imagePath), {
      cachedAt: now,
      signedUrl,
    });
  });

  if (typeof window === "undefined") {
    return;
  }

  try {
    const raw = window.localStorage.getItem(SERVICE_SLIDE_SIGNED_URL_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};

    entries.forEach(({ bucket, imagePath, signedUrl }) => {
      if (!imagePath || !signedUrl) return;

      parsed[getServiceSlideImageCacheKey(bucket, imagePath)] = {
        cachedAt: now,
        signedUrl,
      };
    });

    window.localStorage.setItem(SERVICE_SLIDE_SIGNED_URL_CACHE_KEY, JSON.stringify(parsed));
  } catch (_error) {
    // Signed URLs can always be requested again if local storage is unavailable.
  }
}

async function hydrateServiceSlideImageUrls(items = []) {
  const groupedPaths = new Map();
  const imagePaths = [];

  items.forEach((item) => {
    const slides = Array.isArray(item?.payload?.slides)
      ? item.payload.slides
      : Array.isArray(item?.slides)
        ? item.slides
        : [];

    slides.forEach((slide) => {
      const imagePath = slide?.imagePath || slide?.image_path || "";
      const imageUrl = slide?.imageUrl || slide?.image_url || "";
      if (!imagePath) return;

      const bucket = slide.storageBucket || slide.storage_bucket || DEFAULT_HYMN_STORAGE_BUCKET;
      imagePaths.push({ bucket, imagePath });

      if (!imageUrl) {
        const bucketPaths = groupedPaths.get(bucket) || [];
        bucketPaths.push(imagePath);
        groupedPaths.set(bucket, bucketPaths);
      }
    });
  });

  if (imagePaths.length === 0) {
    return items;
  }

  const signedUrlMap = readServiceSlideSignedUrlCache(imagePaths);
  const signedEntries = [];

  await Promise.all(
    Array.from(groupedPaths.entries()).map(async ([bucket, paths]) => {
      const uniquePaths = Array.from(new Set(paths)).filter(
        (imagePath) => !signedUrlMap.has(getServiceSlideImageCacheKey(bucket, imagePath))
      );

      if (uniquePaths.length === 0) {
        return;
      }

      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrls(uniquePaths, SERVICE_SLIDE_SIGNED_URL_SECONDS);

      if (error) {
        console.error(`Service slide URL signing error for ${bucket}:`, error);
        return;
      }

      (data || []).forEach((entry, index) => {
        if (entry?.signedUrl) {
          const imagePath = uniquePaths[index];
          signedUrlMap.set(getServiceSlideImageCacheKey(bucket, imagePath), entry.signedUrl);
          signedEntries.push({ bucket, imagePath, signedUrl: entry.signedUrl });
        }
      });
    })
  );
  writeServiceSlideSignedUrlCache(signedEntries);

  return items.map((item) => {
    const sourceSlides = Array.isArray(item?.payload?.slides)
      ? item.payload.slides
      : Array.isArray(item?.slides)
        ? item.slides
        : [];

    if (sourceSlides.length === 0) {
      return item;
    }

    const slides = sourceSlides.map((slide) => {
      const imagePath = slide?.imagePath || slide?.image_path || "";
      if (!imagePath) {
        return slide;
      }

      const storageBucket = slide.storageBucket || slide.storage_bucket || DEFAULT_HYMN_STORAGE_BUCKET;
      const imageUrl =
        slide.imageUrl ||
        slide.image_url ||
        signedUrlMap.get(getServiceSlideImageCacheKey(storageBucket, imagePath));

      return imageUrl
        ? {
            ...slide,
            imageUrl,
            imagePath,
            storageBucket,
          }
        : slide;
    });

    return {
      ...item,
      payload: {
        ...(item.payload || {}),
        slides,
      },
      slides,
    };
  });
}

function isOrderOfServiceSource(item = {}) {
  return ORDER_OF_SERVICE_SOURCE_APPS.has(item.sourceApp || item.source_app || "");
}

function isSermonSource(item = {}) {
  return (item.sourceApp || item.source_app || "") === "sermon";
}

function getSongSlotLabel(slotIndex) {
  const normalizedSlotIndex = Number(slotIndex) || 1;
  return `Song ${normalizedSlotIndex}`;
}

function getHymnServiceSlotLabel(slot = {}, sourceItem = {}, fallbackSlotIndex = 1) {
  return (
    String(slot?.label || "").trim() ||
    String(slot?.slotLabel || "").trim() ||
    String(sourceItem?.payload?.slotLabel || "").trim() ||
    String(sourceItem?.slotLabel || "").trim() ||
    getSongSlotLabel(fallbackSlotIndex)
  );
}

function isSongSlotLabel(value = "") {
  return /^song\s+\d+$/i.test(String(value || "").trim());
}

function isInvitationSongSlot(value = {}) {
  const slotIndex = Number(value?.slotIndex || value?.payload?.slotIndex || 0);
  const slotLabel = String(value?.label || value?.slotLabel || value?.payload?.slotLabel || "").trim().toLowerCase();
  return slotIndex === INVITATION_SONG_SLOT_INDEX || slotLabel.includes("invitation");
}

function isInvitationTitlePageItem(item = {}) {
  return (
    item?.payload?.sourceType === "invitation-song-title-page" ||
    item?.payload?.invitationTitlePage === true
  );
}

function getFirstUsefulSlideTitle(slides = []) {
  const slide = (Array.isArray(slides) ? slides : []).find((item) => {
    const title = String(item?.title || "").trim();
    return title && !isSongSlotLabel(title);
  });

  return String(slide?.title || "").trim();
}

function hasImageSlides(slides = []) {
  return (Array.isArray(slides) ? slides : []).some((slide) =>
    Boolean(slide?.imageUrl || slide?.image_url || slide?.imagePath || slide?.image_path)
  );
}

function chooseHymnSlides(hymnSlides = [], sourceSlides = []) {
  if (hasImageSlides(hymnSlides)) {
    return hymnSlides;
  }

  if (hasImageSlides(sourceSlides)) {
    return sourceSlides;
  }

  return Array.isArray(hymnSlides) && hymnSlides.length
    ? hymnSlides
    : Array.isArray(sourceSlides)
      ? sourceSlides
      : [];
}

function getServiceItemSourceId(item = {}) {
  return item.source_id || item.sourceId || item.payload?.hymnId || "";
}

function getPlanItemForSlot(planItems = [], slot = {}) {
  return planItems.find((item) => {
    const itemSlotIndex = Number(item.payload?.slotIndex || item.sort_order || 0);
    return getServiceItemSourceId(item) === slot.hymnId || itemSlotIndex === Number(slot.slotIndex);
  });
}

function buildHymnBlockPayload(slot = {}, sourceItem = {}) {
  const slotIndex = Number(slot.slotIndex || sourceItem?.payload?.slotIndex || sourceItem?.sort_order || 1) || 1;
  const slotLabel = getHymnServiceSlotLabel(slot, sourceItem, slotIndex);
  const hymn = slot.hymn || {};
  const sourceSlides = Array.isArray(sourceItem?.payload?.slides)
    ? sourceItem.payload.slides
    : Array.isArray(sourceItem?.slides)
      ? sourceItem.slides
      : [];
  const hymnSlides = Array.isArray(hymn?.slides) ? hymn.slides : [];
  const slides = chooseHymnSlides(hymnSlides, sourceSlides);
  const sourceTitle = String(sourceItem?.payload?.hymnTitle || sourceItem?.title || "").trim();
  const hymnTitle =
    hymn.title ||
    hymn.name ||
    (!isSongSlotLabel(sourceTitle) ? sourceTitle : "") ||
    getFirstUsefulSlideTitle(slides);
  const songNumber = hymn.songNumber || hymn.song_number || sourceItem?.payload?.songNumber || "";
  const hymnId = slot.hymnId || getServiceItemSourceId(sourceItem);

  return {
    ...(sourceItem?.payload || {}),
    sourceType: HYMN_BLOCK_SOURCE_TYPE,
    slotIndex,
    slotLabel,
    title: slotLabel,
    body: hymnTitle ? [songNumber ? `#${songNumber}` : "", hymnTitle].filter(Boolean).join(" ") : "",
    hymnId,
    hymnTitle,
    songNumber,
    slides,
  };
}

function buildHymnBlockItem(serviceId, sortOrder, slot = {}, sourceItem = {}) {
  const slotIndex = Number(slot.slotIndex || sourceItem?.payload?.slotIndex || sourceItem?.sort_order || sortOrder || 1) || 1;
  const slotLabel = getHymnServiceSlotLabel(slot, sourceItem, slotIndex);
  const payload = buildHymnBlockPayload({ ...slot, slotIndex }, sourceItem);
  const itemId = crypto.randomUUID();

  return {
    id: itemId,
    service_id: serviceId,
    type: "hymn_block",
    source_app: "order-of-service",
    source_id: `hymn-block-song-${slotIndex}`,
    sort_order: sortOrder,
    title: slotLabel,
    payload,
    created_at: new Date().toISOString(),
  };
}

function getHymnDisplayInfo(item = {}) {
  const slotLabel = item.itemType === "hymn"
    ? item.payload?.slotLabel || "Hymn"
    : item.payload?.slotLabel || item.title || "Song";
  const songNumber = item.payload?.songNumber || "";
  const savedTitle = String(item.payload?.hymnTitle || item.title || "").trim();
  const slideTitle = getFirstUsefulSlideTitle(item.slides || item.payload?.slides || []);
  const hymnTitle = !isSongSlotLabel(savedTitle) ? savedTitle : slideTitle;

  return {
    slotLabel,
    songNumber,
    hymnTitle,
    hymnLabel: [songNumber ? `#${songNumber}` : "", hymnTitle || slideTitle].filter(Boolean).join(" "),
  };
}

function getHymnSlotIndex(item = {}) {
  return Number(item.payload?.slotIndex || String(item.source_id || "").replace(/\D/g, "")) || 0;
}

function getHymnBlockSlotIndex(item = {}) {
  if (item.itemType !== "hymn_block" && item.type !== "hymn_block") {
    return 0;
  }

  return Number(item.payload?.slotIndex || String(item.source_id || "").replace(/\D/g, "")) || 0;
}

function buildFallbackItems(userId, serviceId) {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(getServiceStorageKey(userId, serviceId));

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? mergeSermonItemsIntoServiceOrder(parsed.map((item) => normalizeServiceItem(item)))
      : [];
  } catch (error) {
    console.error("Local service queue load error:", error);
    return [];
  }
}

function mergeSermonItemsIntoServiceOrder(items = []) {
  const sermonItems = items.filter(isSermonSource);
  const serviceItems = items.filter(isOrderOfServiceSource);

  if (sermonItems.length === 0) {
    return serviceItems;
  }

  if (serviceItems.length === 0) {
    return sermonItems;
  }

  return serviceItems.map((item) => {
    if (item.itemType !== "sermon_block") return item;

    return {
      ...item,
      payload: {
        ...(item.payload || {}),
        sermonItems,
      },
    };
  });
}

export function getCurrentServiceId() {
  const settings = getServiceSettings();
  return settings.serviceId || "current_service";
}

export async function loadServiceItems(userId, options = {}) {
  const serviceId = getCurrentServiceId();
  const shouldHydrateSlideImages = options?.hydrateSlideImages !== false;

  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .select("*")
    .eq("service_id", serviceId)
    .order("sort_order", { ascending: true });

  if (error) {
    if (!isMissingRelationError(error)) {
      console.error("Service queue load error:", error);
    }

    return {
      serviceId,
      items: shouldHydrateSlideImages
        ? await hydrateServiceSlideImageUrls(buildFallbackItems(userId, serviceId))
        : buildFallbackItems(userId, serviceId),
    };
  }

  const items = Array.isArray(data)
    ? mergeSermonItemsIntoServiceOrder(data.map((item) => normalizeServiceItem(item)))
    : [];

  return {
    serviceId,
    items: shouldHydrateSlideImages ? await hydrateServiceSlideImageUrls(items) : items,
  };
}

function saveFallbackItems(userId, serviceId, items = []) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    getServiceStorageKey(userId, serviceId),
    JSON.stringify(items)
  );
}

export async function updateServiceItem(userId, itemId, updates = {}) {
  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .update(updates)
    .eq("id", itemId)
    .select("*")
    .single();

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const serviceId = getCurrentServiceId();
    const items = buildFallbackItems(userId, serviceId).map((item) =>
      item.id === itemId ? normalizeServiceItem({ ...item, ...updates }) : item
    );
    saveFallbackItems(userId, serviceId, items);

    return items.find((item) => item.id === itemId) || null;
  }

  return normalizeServiceItem(data);
}

export async function deleteQueuedServiceItems(userId) {
  const serviceId = getCurrentServiceId();
  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .select("*")
    .eq("service_id", serviceId);

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const keptItems = buildFallbackItems(userId, serviceId).filter(isOrderOfServiceSource);
    saveFallbackItems(userId, serviceId, keptItems);
    return 0;
  }

  const normalizedItems = Array.isArray(data) ? data.map((item) => normalizeServiceItem(item)) : [];
  const queuedItems = normalizedItems.filter((item) => !isOrderOfServiceSource(item));
  const queuedItemIds = queuedItems.map((item) => item.id).filter(Boolean);

  if (queuedItemIds.length === 0) {
    saveFallbackItems(userId, serviceId, buildFallbackItems(userId, serviceId).filter(isOrderOfServiceSource));
    return 0;
  }

  const { error: deleteError } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .delete()
    .in("id", queuedItemIds);

  if (deleteError) {
    if (!isMissingRelationError(deleteError)) {
      throw deleteError;
    }

    const keptItems = buildFallbackItems(userId, serviceId).filter(isOrderOfServiceSource);
    saveFallbackItems(userId, serviceId, keptItems);
    return queuedItemIds.length;
  }

  const keptItems = normalizedItems.filter(isOrderOfServiceSource);
  saveFallbackItems(userId, serviceId, keptItems);
  return queuedItemIds.length;
}

export async function createOrderOfServiceItem(userId, draft = {}) {
  const serviceId = getCurrentServiceId();
  const currentItems = (await loadServiceItems(userId)).items;
  const now = new Date().toISOString();
  const title = String(draft.title || "").trim();
  const itemId = crypto.randomUUID();
  const payload = {
    title,
    body: "",
    sourceType: "order-of-service",
    slides: [
      {
        id: `${itemId}-slide`,
        type: "custom-slide",
        title,
        body: "",
      },
    ],
  };
  const nextItem = {
    id: itemId,
    service_id: serviceId,
    type: "custom_slide",
    source_app: "order-of-service",
    source_id: itemId,
    sort_order: Math.max(...currentItems.map((item) => Number(item.sort_order) || 0), 0) + 1,
    title,
    payload,
    created_at: now,
  };

  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .insert(nextItem)
    .select("*")
    .single();

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const items = [...buildFallbackItems(userId, serviceId), normalizeServiceItem(nextItem)];
    saveFallbackItems(userId, serviceId, items);
    return normalizeServiceItem(nextItem);
  }

  return normalizeServiceItem(data);
}

export async function createHymnTitlePageItem(userId, slot = {}) {
  const serviceId = getCurrentServiceId();
  const currentItems = (await loadServiceItems(userId)).items;
  const now = new Date().toISOString();
  const itemId = crypto.randomUUID();
  const slotIndex = Number(slot.slotIndex || 0) || 0;
  const slotLabel = slotIndex ? getHymnServiceSlotLabel(slot, {}, slotIndex) : "Hymn";
  const hymn = slot.hymn || {};
  const hymnTitle = String(hymn.title || slot.hymnTitle || slot.title || "").trim();
  const songNumber = String(hymn.songNumber || hymn.song_number || slot.songNumber || slot.song_number || "").trim();
  const isInvitationAnnouncement = Boolean(slot.invitationTitlePage);
  const payload = {
    title: isInvitationAnnouncement
      ? "Invitation Song Announcement"
      : slotIndex
        ? `${slotLabel} Title page`
        : "Hymn Title page",
    body: isInvitationAnnouncement
      ? "Shows the invitation song number"
      : slotIndex
        ? `Pulls from ${slotLabel}`
        : "Pulls from the next hymn block",
    sourceType: isInvitationAnnouncement ? "invitation-song-title-page" : "hymn-title-page",
    invitationTitlePage: isInvitationAnnouncement,
    slotIndex,
    slotLabel,
    hymnId: slot.hymnId || "",
    hymnTitle,
    songNumber,
  };
  const nextItem = {
    id: itemId,
    service_id: serviceId,
    type: "hymn_title_page",
    source_app: "order-of-service",
    source_id: slotIndex ? `hymn-title-song-${slotIndex}` : itemId,
    sort_order: Math.max(...currentItems.map((item) => Number(item.sort_order) || 0), 0) + 1,
    title: payload.title,
    payload,
    created_at: now,
  };

  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .insert(nextItem)
    .select("*")
    .single();

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const items = [...buildFallbackItems(userId, serviceId), normalizeServiceItem(nextItem)];
    saveFallbackItems(userId, serviceId, items);
    return normalizeServiceItem(nextItem);
  }

  return normalizeServiceItem(data);
}

export async function createPreacherSlideItem(userId, draft = {}) {
  const serviceId = getCurrentServiceId();
  const currentItems = (await loadServiceItems(userId)).items;
  const now = new Date().toISOString();
  const preacherName = String(draft.name || draft.title || "").trim();
  const itemId = crypto.randomUUID();
  const payload = {
    title: "Preacher",
    body: preacherName,
    sourceType: "preacher-slide",
  };
  const nextItem = {
    id: itemId,
    service_id: serviceId,
    type: "preacher_slide",
    source_app: "order-of-service",
    source_id: itemId,
    sort_order: Math.max(...currentItems.map((item) => Number(item.sort_order) || 0), 0) + 1,
    title: "Preacher",
    payload,
    created_at: now,
  };

  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .insert(nextItem)
    .select("*")
    .single();

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const items = [...buildFallbackItems(userId, serviceId), normalizeServiceItem(nextItem)];
    saveFallbackItems(userId, serviceId, items);
    return normalizeServiceItem(nextItem);
  }

  return normalizeServiceItem(data);
}

export async function createSermonBlockItem(userId) {
  const serviceId = getCurrentServiceId();
  const currentItems = (await loadServiceItems(userId)).items;
  const now = new Date().toISOString();
  const itemId = crypto.randomUUID();
  const payload = {
    title: "Sermon",
    body: "Sermon slides live from iPad",
    sourceType: "sermon-block",
  };
  const nextItem = {
    id: itemId,
    service_id: serviceId,
    type: "sermon_block",
    source_app: "order-of-service",
    source_id: itemId,
    sort_order: Math.max(...currentItems.map((item) => Number(item.sort_order) || 0), 0) + 1,
    title: "Sermon",
    payload,
    created_at: now,
  };

  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .insert(nextItem)
    .select("*")
    .single();

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const items = [...buildFallbackItems(userId, serviceId), normalizeServiceItem(nextItem)];
    saveFallbackItems(userId, serviceId, items);
    return normalizeServiceItem(nextItem);
  }

  return normalizeServiceItem(data);
}

export async function createClockSlideItem(userId) {
  const serviceId = getCurrentServiceId();
  const currentItems = (await loadServiceItems(userId)).items;
  const now = new Date().toISOString();
  const itemId = crypto.randomUUID();
  const payload = {
    title: "Clock",
    body: "",
    sourceType: "clock-slide",
  };
  const nextItem = {
    id: itemId,
    service_id: serviceId,
    type: "custom_slide",
    source_app: "order-of-service",
    source_id: itemId,
    sort_order: Math.max(...currentItems.map((item) => Number(item.sort_order) || 0), 0) + 1,
    title: "Clock",
    payload,
    created_at: now,
  };

  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .insert(nextItem)
    .select("*")
    .single();

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const items = [...buildFallbackItems(userId, serviceId), normalizeServiceItem(nextItem)];
    saveFallbackItems(userId, serviceId, items);
    return normalizeServiceItem(nextItem);
  }

  return normalizeServiceItem(data);
}

export async function createStartServiceSlideItem(userId) {
  const serviceId = getCurrentServiceId();
  const currentItems = (await loadServiceItems(userId)).items;
  const now = new Date().toISOString();
  const itemId = crypto.randomUUID();
  const payload = {
    title: "Start Service",
    body: "",
    sourceType: "start-service-slide",
  };
  const nextItem = {
    id: itemId,
    service_id: serviceId,
    type: "custom_slide",
    source_app: "order-of-service",
    source_id: itemId,
    sort_order: Math.max(...currentItems.map((item) => Number(item.sort_order) || 0), 0) + 1,
    title: payload.title,
    payload,
    created_at: now,
  };

  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .insert(nextItem)
    .select("*")
    .single();

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const items = [...buildFallbackItems(userId, serviceId), normalizeServiceItem(nextItem)];
    saveFallbackItems(userId, serviceId, items);
    return normalizeServiceItem(nextItem);
  }

  return normalizeServiceItem(data);
}

export async function createThankYouSlideItem(userId) {
  const serviceId = getCurrentServiceId();
  const currentItems = (await loadServiceItems(userId)).items;
  const now = new Date().toISOString();
  const itemId = crypto.randomUUID();
  const payload = {
    title: "End of Service",
    body: "",
    sourceType: "end-service-slide",
  };
  const nextItem = {
    id: itemId,
    service_id: serviceId,
    type: "custom_slide",
    source_app: "order-of-service",
    source_id: itemId,
    sort_order: Math.max(...currentItems.map((item) => Number(item.sort_order) || 0), 0) + 1,
    title: payload.title,
    payload,
    created_at: now,
  };

  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .insert(nextItem)
    .select("*")
    .single();

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const items = [...buildFallbackItems(userId, serviceId), normalizeServiceItem(nextItem)];
    saveFallbackItems(userId, serviceId, items);
    return normalizeServiceItem(nextItem);
  }

  return normalizeServiceItem(data);
}

export async function addServiceItemsToCurrentService(userId, sourceItems = []) {
  const serviceId = getCurrentServiceId();
  const currentItems = (await loadServiceItems(userId)).items;
  const maxSortOrder = Math.max(...currentItems.map((item) => Number(item.sort_order) || 0), 0);
  const existingHymnSourceIds = new Set(
    currentItems
      .filter((item) => (item.sourceApp || item.source_app) === "hymns")
      .map((item) => item.source_id || item.sourceId || item.payload?.hymnId || "")
      .filter(Boolean)
  );
  const nextItems = sourceItems
    .filter((item) => item?.type === "hymn" || item?.itemType === "hymn")
    .filter((item) => {
      const sourceId = item.source_id || item.sourceId || item.payload?.hymnId || "";
      return sourceId && !existingHymnSourceIds.has(sourceId);
    })
    .map((item, index) => {
      const sourceId = item.source_id || item.sourceId || item.payload?.hymnId || "";
      const slotIndex = Number(item.payload?.slotIndex || item.sort_order || index + 1) || index + 1;
      const slotLabel = `Song ${slotIndex}`;

      return {
        id: crypto.randomUUID(),
        service_id: serviceId,
        type: "hymn",
        source_app: "hymns",
        source_id: sourceId,
        sort_order: maxSortOrder + index + 1,
        title: item.title || item.payload?.hymnTitle || "Hymn",
        payload: {
          ...(item.payload || {}),
          hymnId: sourceId,
          slotIndex,
          slotLabel,
          slides: Array.isArray(item.payload?.slides)
            ? item.payload.slides
            : Array.isArray(item.slides)
              ? item.slides
              : [],
        },
        created_at: new Date().toISOString(),
      };
    });

  if (nextItems.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .insert(nextItems)
    .select("*");

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const items = [...buildFallbackItems(userId, serviceId), ...nextItems.map((item) => normalizeServiceItem(item))];
    saveFallbackItems(userId, serviceId, items);
    return nextItems.map((item) => normalizeServiceItem(item));
  }

  return Array.isArray(data) ? data.map((item) => normalizeServiceItem(item)) : [];
}

export async function addHymnBlocksToCurrentService(userId, slots = [], planItems = []) {
  const serviceId = getCurrentServiceId();
  const currentItems = (await loadServiceItems(userId)).items;
  const maxSortOrder = Math.max(...currentItems.map((item) => Number(item.sort_order) || 0), 0);
  const existingBlockSlots = new Set(
    currentItems
      .map((item) => getHymnBlockSlotIndex(item))
      .filter(Boolean)
  );
  const selectedSlots = (Array.isArray(slots) ? slots : [])
    .filter((slot) => slot?.hymnId)
    .sort((left, right) => Number(left.slotIndex || 0) - Number(right.slotIndex || 0));
  const nextItems = selectedSlots
    .filter((slot) => !existingBlockSlots.has(Number(slot.slotIndex)))
    .map((slot, index) =>
      buildHymnBlockItem(
        serviceId,
        maxSortOrder + index + 1,
        slot,
        getPlanItemForSlot(planItems, slot)
      )
    );

  if (nextItems.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .insert(nextItems)
    .select("*");

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const items = [...buildFallbackItems(userId, serviceId), ...nextItems.map((item) => normalizeServiceItem(item))];
    saveFallbackItems(userId, serviceId, items);
    return nextItems.map((item) => normalizeServiceItem(item));
  }

  return Array.isArray(data) ? data.map((item) => normalizeServiceItem(item)) : [];
}

export async function syncHymnBlocksWithWeeklyPlan(userId, plan = {}) {
  const serviceId = getCurrentServiceId();
  const currentItems = (await loadServiceItems(userId)).items;
  const planItems = Array.isArray(plan?.items) ? plan.items : [];
  const slots = Array.isArray(plan?.slots) ? plan.slots : [];
  const slotByIndex = new Map(
    slots.map((slot) => [Number(slot?.slotIndex || 0), slot]).filter(([slotIndex]) => Boolean(slotIndex))
  );
  const hymnBlockItems = currentItems.filter((item) => getHymnBlockSlotIndex(item));
  const updates = hymnBlockItems.map((item) => {
    const slotIndex = getHymnBlockSlotIndex(item);
    const slot = slotByIndex.get(slotIndex) || { slotIndex };
    const sourceItem = getPlanItemForSlot(planItems, slot) || {};
    const payload = buildHymnBlockPayload({ ...slot, slotIndex }, sourceItem);

    return normalizeServiceItem({
      ...item,
      service_id: item.service_id || serviceId,
      type: "hymn_block",
      source_app: "order-of-service",
      source_id: `hymn-block-song-${slotIndex}`,
      title: getSongSlotLabel(slotIndex),
      payload,
    });
  });

  if (updates.length === 0) {
    return currentItems;
  }

  const { error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .upsert(
      updates.map((item) => ({
        id: item.id,
        service_id: item.service_id || serviceId,
        type: "hymn_block",
        source_app: "order-of-service",
        source_id: item.source_id || item.sourceId || `hymn-block-song-${item.payload?.slotIndex || 1}`,
        sort_order: item.sort_order,
        title: item.title || "",
        payload: item.payload || {},
        created_at: item.created_at || new Date().toISOString(),
      })),
      { onConflict: "id" }
    );

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const nextItems = currentItems.map((item) => updates.find((updated) => updated.id === item.id) || item);
    saveFallbackItems(userId, serviceId, nextItems);
    return nextItems;
  }

  return hydrateServiceSlideImageUrls(
    currentItems.map((item) => updates.find((updated) => updated.id === item.id) || item)
  );
}

export async function updateOrderOfServiceItem(userId, item, draft = {}) {
  const title = String(draft.title || "").trim();
  const payload = {
    ...(item?.payload || {}),
    title,
    body: "",
    sourceType: "order-of-service",
    slides: [
      {
        id: `${item?.id || crypto.randomUUID()}-slide`,
        type: "custom-slide",
        title,
        body: "",
      },
    ],
  };

  return updateServiceItem(userId, item.id, {
    title,
    payload,
  });
}

export async function deleteServiceItem(userId, itemId) {
  const { error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .delete()
    .eq("id", itemId);

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const serviceId = getCurrentServiceId();
    const items = buildFallbackItems(userId, serviceId).filter((item) => item.id !== itemId);
    saveFallbackItems(userId, serviceId, items);
  }
}

export async function updateServiceItemOrder(userId, orderedItems = []) {
  const serviceId = getCurrentServiceId();
  const updates = orderedItems.map((item, index) => ({
    ...item,
    sort_order: index + 1,
  }));

  const { error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .upsert(
      updates.map((item) => ({
        id: item.id,
        service_id: item.service_id || serviceId,
        type: item.type,
        source_app: item.source_app || item.sourceApp || "service",
        source_id: item.source_id || item.sourceId || item.id,
        sort_order: item.sort_order,
        title: item.title || "",
        payload: item.payload || {},
        created_at: item.created_at || new Date().toISOString(),
      })),
      { onConflict: "id" }
    );

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    saveFallbackItems(userId, serviceId, updates);
  }

  return updates.map((item) => normalizeServiceItem(item));
}

export async function updateServiceItemSlides(userId, item, slides = []) {
  const nextPayload = {
    ...(item?.payload || {}),
    slides,
  };

  return updateServiceItem(userId, item.id, {
    payload: nextPayload,
  });
}

export function getItemLabel(item) {
  if (item.itemType === "sermon_block") {
    return "Sermon";
  }

  if (item.itemType === "preacher_slide") {
    return item.payload?.body ? `Preacher: ${item.payload.body}` : "Preacher";
  }

  if (item.itemType === "hymn_title_page") {
    return "Hymn Title page";
  }

  if (item.itemType === "hymn_block") {
    const slotLabel = item.payload?.slotLabel || item.title || "Song";
    const songNumber = item.payload?.songNumber || "";
    const hymnTitle = item.payload?.hymnTitle || "";
    const hymnLabel = [songNumber ? `#${songNumber}` : "", hymnTitle].filter(Boolean).join(" ");
    return hymnLabel ? `${slotLabel}: ${hymnLabel}` : slotLabel;
  }

  if (item.itemType === "hymn") {
    const songNumber = item.payload?.songNumber || "";
    const label = item.title || item.payload?.hymnTitle || "Hymn";
    return songNumber ? `#${songNumber} ${label}` : label;
  }

  if (item.itemType === "verse") {
    return item.title || item.payload?.reference || "Scripture";
  }

  if (item.itemType === "custom_slide") {
    if (item.payload?.sourceType === "start-service-slide") {
      return "Start Service";
    }

    if (item.payload?.sourceType === "clock-slide") {
      return "Clock slide";
    }

    if (item.payload?.sourceType === "end-service-slide" || item.payload?.sourceType === "thank-you-slide") {
      return "End of Service";
    }

    return item.title || item.payload?.title || "Custom Slide";
  }

  return item.title || item.payload?.title || "Title Slide";
}

export function getItemMeta(item) {
  const slideCount = Array.isArray(item.slides) ? item.slides.length : 0;

  if (item.itemType === "hymn_title_page") {
    return item.payload?.slotLabel
      ? `Pulls ${item.payload.slotLabel} name and number`
      : "Pulls the next hymn name and number";
  }

  if (item.itemType === "preacher_slide") {
    return "Preacher slide";
  }

  if (item.itemType === "sermon_block") {
    return "Returns to the next service slide after sermon";
  }

  if (item.itemType === "hymn_block") {
    return item.payload?.hymnTitle
      ? `${slideCount} selected hymn slide${slideCount === 1 ? "" : "s"}`
      : "Hymn selector block";
  }

  if (item.itemType === "hymn") {
    return `${slideCount} hymn slide${slideCount === 1 ? "" : "s"}`;
  }

  if (item.itemType === "verse") {
    return `${slideCount} scripture slide${slideCount === 1 ? "" : "s"}`;
  }

  if (item.itemType === "custom_slide") {
    if (item.payload?.sourceType === "start-service-slide") {
      return "Welcome slide";
    }

    if (item.payload?.sourceType === "clock-slide") {
      return "Live clock";
    }

    if (item.payload?.sourceType === "end-service-slide" || item.payload?.sourceType === "thank-you-slide") {
      return "Closing thank you slide";
    }

    return "Custom sermon slide";
  }

  return "Sermon title slide";
}

export function buildSlidesFromServiceItems(items) {
  return items.flatMap((item, itemIndex) => {
    if (item.itemType === "sermon_block") {
      const sermonBlockId = item.id || item.source_id;
      const sermonItems = Array.isArray(item.payload?.sermonItems)
        ? item.payload.sermonItems.map((sermonItem) => normalizeServiceItem(sermonItem))
        : [];
      const sermonSlides = sermonItems.length > 0
        ? buildSlidesFromServiceItems(sermonItems).map((slide) => ({
            ...slide,
            serviceBlockParentId: sermonBlockId,
            sourceType: slide.sourceType || "sermon-service",
          }))
        : [];

      if (sermonSlides.length > 0) {
        return sermonSlides;
      }

      return [
        {
          id: `${sermonBlockId}-sermon-block`,
          parentId: sermonBlockId,
          itemType: "custom_slide",
          title: item.payload?.title || item.title || "Sermon",
          body: item.payload?.body || "Sermon slides live from iPad",
        },
      ];
    }

    if (item.itemType === "preacher_slide") {
      return [
        {
          id: `${item.id || item.source_id}-preacher-slide`,
          parentId: item.id || item.source_id,
          itemType: "custom_slide",
          title: item.payload?.title || item.title || "Preacher",
          body: item.payload?.body || "",
        },
      ];
    }

    if (item.itemType === "hymn_title_page") {
      const targetSlotIndex = Number(item.payload?.slotIndex || 0) || 0;
      const nextHymnItem = targetSlotIndex
        ? items.find(
            (nextItem) =>
              (nextItem.itemType === "hymn_block" || nextItem.itemType === "hymn") &&
              getHymnSlotIndex(nextItem) === targetSlotIndex
          )
        : items
            .slice(itemIndex + 1)
            .find((nextItem) => nextItem.itemType === "hymn_block" || nextItem.itemType === "hymn");
      const hymnInfo = nextHymnItem
        ? getHymnDisplayInfo(nextHymnItem)
        : {
            slotLabel: item.payload?.slotLabel || "Hymn",
            songNumber: item.payload?.songNumber || "",
            hymnLabel: [
              item.payload?.songNumber ? `#${item.payload.songNumber}` : "",
              item.payload?.hymnTitle || "",
            ].filter(Boolean).join(" "),
            hymnTitle: item.payload?.hymnTitle || "No hymn selected",
      };
      const songNumberLabel = hymnInfo.songNumber ? String(hymnInfo.songNumber) : "";
      const hymnTitle = hymnInfo.hymnTitle || hymnInfo.hymnLabel || hymnInfo.slotLabel;
      const isInvitationTitlePage = isInvitationSongSlot({
        ...item,
        label: hymnInfo.slotLabel,
        slotLabel: hymnInfo.slotLabel || item.payload?.slotLabel,
      }) && isInvitationTitlePageItem(item);

      return [
        {
          id: `${item.id || item.source_id}-hymn-title-page`,
          parentId: item.id || item.source_id,
          itemType: "custom_slide",
          title: isInvitationTitlePage
            ? "Invitation Song"
            : hymnInfo.hymnTitle || hymnInfo.hymnLabel || hymnInfo.slotLabel,
          body: isInvitationTitlePage
            ? songNumberLabel
            : songNumberLabel,
        },
      ];
    }

    if (item.itemType === "hymn_block") {
      const slotLabel = item.payload?.slotLabel || item.title || "Song";
      const hymnTitle = item.payload?.hymnTitle || "";
      const songNumber = item.payload?.songNumber || "";
      const hymnSlides = Array.isArray(item.slides) && item.slides.length
        ? item.slides
        : Array.isArray(item.payload?.slides)
          ? item.payload.slides
          : [];

      if (hymnSlides.length > 0) {
        return hymnSlides.map((slide, index) => ({
          id: `${item.id || item.source_id}-hymn-block-${index}`,
          parentId: item.id || item.source_id,
          itemType: "hymn",
          title: slide.title || hymnTitle || slotLabel,
          body: slide.body || slide.text || "",
          imageUrl: slide.imageUrl || slide.image_url || "",
          imagePath: slide.imagePath || slide.image_path || "",
          storageBucket: slide.storageBucket || slide.storage_bucket || "global-hymn-files",
          songNumber,
          isEndOfSong:
            typeof slide.isEndOfSong === "boolean"
              ? slide.isEndOfSong
              : typeof slide.is_end_of_song === "boolean"
                ? slide.is_end_of_song
                : index === hymnSlides.length - 1,
        }));
      }

      return [
        {
          id: `${item.id || item.source_id}-hymn-block-empty`,
          parentId: item.id || item.source_id,
          itemType: "custom_slide",
          title: item.payload?.slotLabel || item.title || "Song",
          body: "No hymn selected",
        },
      ];
    }

    if (item.itemType === "hymn") {
      const hymnSlides = Array.isArray(item.slides) && item.slides.length
        ? item.slides
        : Array.isArray(item.payload?.slides)
          ? item.payload.slides
          : [];

      return hymnSlides.map((slide, index) => ({
        id: `${item.id || item.source_id}-hymn-${index}`,
        parentId: item.id || item.source_id,
        itemType: "hymn",
        title: slide.title || item.title || item.payload?.hymnTitle || "Hymn",
        body: slide.body || slide.text || "",
        imageUrl: slide.imageUrl || slide.image_url || "",
        imagePath: slide.imagePath || slide.image_path || "",
        storageBucket: slide.storageBucket || slide.storage_bucket || "global-hymn-files",
        songNumber: item.payload?.songNumber || "",
        isEndOfSong:
          typeof slide.isEndOfSong === "boolean"
            ? slide.isEndOfSong
            : typeof slide.is_end_of_song === "boolean"
              ? slide.is_end_of_song
              : index === hymnSlides.length - 1,
      }));
    }

    if (item.itemType === "title_slide") {
      return (item.slides || []).map((slide, index) => ({
        id: `${item.id || item.source_id}-title-${index}`,
        parentId: item.id || item.source_id,
        itemType: "title_slide",
        title: slide.title || item.title || "Title Slide",
        subtitle: slide.subtitle || "",
        date: slide.date || "",
      }));
    }

    if (item.itemType === "verse") {
      return (item.slides || []).map((slide, index) => ({
        id: `${item.id || item.source_id}-verse-${index}`,
        parentId: item.id || item.source_id,
        itemType: "verse",
        title: slide.title || item.title || "Scripture",
        reference: slide.reference || item.title || "",
        verseNumber: slide.verseNumber || "",
        text: slide.text || "",
      }));
    }

    if (item.itemType === "visual_slide") {
      return [
        {
          id: `${item.id || item.source_id}-visual`,
          parentId: item.id || item.source_id,
          itemType: "visual_slide",
          sourceType: item.payload?.sourceType || "sermon-visual",
          title: item.payload?.title || item.title || "Visual Slide",
          subtitle: item.payload?.caption || "",
          body: item.payload?.caption || "",
          imageUrl: item.payload?.imageUrl || item.imageUrl || "",
          imagePath: item.payload?.imagePath || item.imagePath || "",
          storageBucket: item.payload?.storageBucket || item.storageBucket || "church-media-library",
        },
      ];
    }

    return [
      {
        id: `${item.id || item.source_id}-custom`,
        parentId: item.id || item.source_id,
        itemType: "custom_slide",
        sourceType: item.payload?.sourceType || "",
        title: item.payload?.title || item.title || "Custom Slide",
        body: item.payload?.body || "",
      },
    ];
  });
}

export async function getServiceResumeIndexAfterSermonBlock(userId) {
  const { items } = await loadServiceItems(userId);
  const slides = buildSlidesFromServiceItems(items);
  const sermonItem = items.find((item) => item.itemType === "sermon_block");

  if (!sermonItem) {
    return {
      currentSlideIndex: 0,
      slides,
    };
  }

  const sermonSlideIndex = slides.findIndex(
    (slide) =>
      slide.parentId === (sermonItem.id || sermonItem.source_id) ||
      slide.serviceBlockParentId === (sermonItem.id || sermonItem.source_id)
  );

  return {
    currentSlideIndex: sermonSlideIndex >= 0 ? sermonSlideIndex : 0,
    slides,
  };
}
